/**
 * Document Upload Component
 * Provides drag-and-drop and file picker UI for document uploads
 * Shows upload progress and status for each file
 */

import React, { useRef, useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  LinearProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { FileUploadStatus, UPLOAD_CONFIG } from '../../types/documentUpload';

// Styled components
const DropZone = styled(Paper, { 
  shouldForwardProp: (prop) => prop !== 'isDragActive' 
})<{ isDragActive?: boolean }>(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  marginBottom: theme.spacing(2)
}));

const FileListContainer = styled(Box)(({ theme }) => ({
  maxHeight: 300,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.grey[200],
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
    borderRadius: 4,
  }
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 4,
  borderRadius: 2,
  backgroundColor: theme.palette.grey[300],
  '& .MuiLinearProgress-bar': {
    borderRadius: 2,
    backgroundColor: theme.colors.candelion,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

interface DocumentUploadProps {
  files: FileUploadStatus[];
  onSelectFiles: (files: FileList | File[]) => void;
  onRemoveFile: (fileId: string) => void;
  onStartUpload: () => void;
  isUploading: boolean;
  disabled?: boolean;
  maxFiles?: number;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  files,
  onSelectFiles,
  onRemoveFile,
  onStartUpload,
  isUploading,
  disabled = false,
  maxFiles = UPLOAD_CONFIG.MAX_FILES_PER_BATCH
}) => {
  const { t } = useUntypedTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Translate validation error keys to user-friendly messages
  const translateError = (errorKey: string): string => {
    switch (errorKey) {
      case UPLOAD_CONFIG.VALIDATION_ERRORS.FILE_TOO_LARGE:
        return t('documentUpload.errors.fileTooLarge', 'File size exceeds {{size}}MB limit', { size: UPLOAD_CONFIG.MAX_FILE_SIZE_MB });
      case UPLOAD_CONFIG.VALIDATION_ERRORS.UNSUPPORTED_TYPE:
        return t('documentUpload.errors.unsupportedType', 'File type not supported. Allowed: PDF, DOCX, TXT, Markdown');
      case UPLOAD_CONFIG.VALIDATION_ERRORS.TOO_MANY_FILES:
        return t('documentUpload.errors.tooManyFiles', 'Maximum {{count}} files allowed per upload', { count: UPLOAD_CONFIG.MAX_FILES_PER_BATCH });
      case UPLOAD_CONFIG.VALIDATION_ERRORS.FILE_EMPTY:
        return t('documentUpload.errors.fileEmpty', 'File is empty');
      case UPLOAD_CONFIG.VALIDATION_ERRORS.UPLOAD_FAILED:
        return t('documentUpload.errors.uploadFailed', 'Failed to upload file');
      case UPLOAD_CONFIG.VALIDATION_ERRORS.PROCESSING_FAILED:
        return t('documentUpload.errors.processingFailed', 'Failed to process document');
      default:
        return errorKey; // Fallback to error key if not found
    }
  };

  // Get file icon based on MIME type
  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') {
      return <PictureAsPdfIcon />;
    }
    if (mimeType.includes('word') || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <DescriptionIcon />;
    }
    return <InsertDriveFileIcon />;
  };

  // Get status icon
  const getStatusIcon = (status: FileUploadStatus['status'], error?: string) => {
    switch (status) {
      case 'done':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return null;
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled || isUploading) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      onSelectFiles(droppedFiles);
    }
  }, [disabled, isUploading, onSelectFiles]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onSelectFiles(e.target.files);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    }
  }, [onSelectFiles]);

  // Open file picker
  const openFilePicker = useCallback(() => {
    if (!disabled && !isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled, isUploading]);

  // Calculate if we can add more files
  const canAddMoreFiles = files.length < maxFiles;
  const remainingSlots = maxFiles - files.length;

  // Check if there are valid files to upload
  const hasValidFiles = files.some(f => f.status === 'idle' && !f.error);

  // Check if user selected too many files (some were rejected)
  const hasTooManyFilesError = files.some(f => f.error === UPLOAD_CONFIG.VALIDATION_ERRORS.TOO_MANY_FILES);

  return (
    <Box>
      {/* Warning when too many files were selected */}
      {hasTooManyFilesError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('documentUpload.tooManyFilesWarning', 
            'You selected more than 10 files. Only the first 10 files were added. Please upload in batches.'
          )}
        </Alert>
      )}

      {/* Drop Zone */}
      {canAddMoreFiles && (
        <DropZone
          isDragActive={isDragActive}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={openFilePicker}
          sx={{ 
            opacity: disabled || isUploading ? 0.6 : 1,
            cursor: disabled || isUploading ? 'not-allowed' : 'pointer'
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {t('documentUpload.dragDropText', 'Drag and drop files here or click to browse')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('documentUpload.allowedTypes', 'PDF, DOCX, TXT, Markdown (max {{size}}MB per file)', { size: UPLOAD_CONFIG.MAX_FILE_SIZE_MB })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('documentUpload.remainingSlots', '{{count}} file slots remaining', { count: remainingSlots })}
          </Typography>
        </DropZone>
      )}

      {/* Hidden file input */}
      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/x-markdown"
        onChange={handleFileInputChange}
        disabled={disabled || isUploading || !canAddMoreFiles}
      />

      {/* File list */}
      {files.length > 0 && (
        <FileListContainer>
          <List dense>
            {files.map((file) => (
              <ListItem key={file.id}>
                <ListItemIcon>
                  {getFileIcon(file.mimeType)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {file.name}
                      </Typography>
                      {getStatusIcon(file.status, file.error)}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                        {file.error && (
                          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                            {translateError(file.error)}
                          </Typography>
                        )}
                      </Typography>
                      {(file.status === 'uploading' || file.status === 'processing') && (
                        <Box sx={{ mt: 0.5 }}>
                          <ProgressBar
                            variant={file.status === 'uploading' ? 'determinate' : 'indeterminate'}
                            value={file.status === 'uploading' ? file.progress : undefined}
                          />
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: '0.65rem', 
                              display: 'block', 
                              mt: 0.5,
                              fontWeight: 500
                            }}
                          >
                            {file.status === 'uploading' 
                              ? `${t('documentUpload.uploading')} ${Math.round(file.progress)}%`
                              : t('documentUpload.processing')
                            }
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => onRemoveFile(file.id)}
                    disabled={file.status === 'uploading' || file.status === 'processing'}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </FileListContainer>
      )}

      {/* Upload button */}
      {files.length > 0 && hasValidFiles && !isUploading && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onStartUpload}
            startIcon={<CloudUploadIcon />}
            disabled={disabled}
          >
            {t('documentUpload.uploadButton', 'Upload Files')}
          </Button>
        </Box>
      )}

      {/* Upload in progress message */}
      {isUploading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('documentUpload.uploadingMessage', 'Uploading files... Please do not close this page.')}
        </Alert>
      )}

      {/* All files uploaded message */}
      {files.length > 0 && 
       !hasValidFiles && 
       !isUploading && 
       files.every(f => f.status === 'done' || f.status === 'error') && (
        <Alert 
          severity={files.some(f => f.status === 'error') ? 'warning' : 'success'} 
          sx={{ mt: 2 }}
        >
          {files.some(f => f.status === 'error')
            ? t('documentUpload.uploadCompleteWithErrors', 'Upload failed')
            : t('documentUpload.uploadComplete', 'All files uploaded successfully!')
          }
        </Alert>
      )}
    </Box>
  );
};

export default DocumentUpload;
